import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { Helmet } from '@/pages/home/components/SEOHead';
import { CategorySchema } from '@/components/feature/SchemaOrg';
import { categories, articles, type Article } from '@/mocks/categories';

export default function Category() {
  const [activeCategory, setActiveCategory] = useState('news');

  const filteredArticles = useMemo(() => {
    return articles.filter((a) => a.categoryId === activeCategory);
  }, [activeCategory]);

  const currentCategory = categories.find((c) => c.id === activeCategory);

  const getArticleImage = (article: Article) => {
    const seqMap: Record<string, string> = {
      'article-award-2026': 'https://readdy.ai/api/search-image?query=A%20prestigious%20award%20ceremony%20scene%20with%20golden%20trophy%20on%20podium%2C%20soft%20warm%20spotlight%2C%20elegant%20dark%20background%20with%20subtle%20gold%20accents%2C%20professional%20corporate%20atmosphere%2C%20achievement%20and%20excellence%20theme%2C%20clean%20and%20sophisticated&width=800&height=450&seq=article-award-2026&orientation=landscape',
      'article-v5-launch': 'https://readdy.ai/api/search-image?query=A%20modern%20digital%20interface%20launch%20concept%20with%20clean%20UI%20elements%2C%20glowing%20version%20number%20five%20symbol%2C%20dark%20minimalist%20background%20with%20subtle%20golden%20highlights%2C%20technology%20upgrade%20theme%2C%20sleek%20and%20professional&width=800&height=450&seq=article-v5-launch&orientation=landscape',
      'article-beginner-guide': 'https://readdy.ai/api/search-image?query=A%20warm%20and%20welcoming%20onboarding%20scene%20with%20hand%20pointing%20at%20clean%20modern%20interface%2C%20friendly%20step%20by%20step%20guide%20visual%2C%20soft%20warm%20lighting%2C%20dark%20background%20with%20golden%20accents%2C%20beginner%20friendly%20atmosphere&width=800&height=450&seq=article-beginner-guide&orientation=landscape',
      'article-faq': 'https://readdy.ai/api/search-image?query=An%20organized%20frequently%20asked%20questions%20concept%20with%20clean%20typography%20cards%2C%20floating%20question%20mark%20icons%20in%20golden%20color%2C%20dark%20minimalist%20background%2C%20professional%20help%20desk%20theme%2C%20warm%20and%20approachable&width=800&height=450&seq=article-faq&orientation=landscape',
      'article-security': 'https://readdy.ai/api/search-image?query=A%20digital%20security%20shield%20concept%20with%20abstract%20lock%20and%20encryption%20symbols%2C%20glowing%20protective%20layers%2C%20dark%20background%20with%20warm%20golden%20accents%2C%20cybersecurity%20professional%20theme%2C%20trustworthy%20and%20robust%20atmosphere&width=800&height=450&seq=article-security&orientation=landscape',
      'article-vip-upgrade': 'https://readdy.ai/api/search-image?query=A%20premium%20crown%20icon%20floating%20with%20sparkling%20golden%20particles%2C%20luxurious%20dark%20background%2C%20VIP%20membership%20elite%20concept%2C%20soft%20warm%20glowing%20highlights%2C%20exclusive%20and%20prestigious%20atmosphere&width=800&height=450&seq=article-vip-upgrade&orientation=landscape',
      'article-summer-event': 'https://readdy.ai/api/search-image?query=A%20vibrant%20summer%20celebration%20concept%20with%20colorful%20confetti%20and%20gift%20boxes%2C%20warm%20golden%20lighting%2C%20dark%20background%20with%20festive%20atmosphere%2C%20anniversary%20event%20theme%2C%20joyful%20and%20exciting&width=800&height=450&seq=article-summer-event&orientation=landscape',
      'article-loyalty-reward': 'https://readdy.ai/api/search-image?query=A%20warm%20heartfelt%20thank%20you%20concept%20with%20golden%20ribbon%20and%20star%20shapes%2C%20appreciation%20theme%2C%20dark%20elegant%20background%20with%20soft%20glow%2C%20loyalty%20reward%20concept%2C%20grateful%20and%20genuine%20atmosphere&width=800&height=450&seq=article-loyalty-reward&orientation=landscape',
    };
    return seqMap[article.imageSeq] || seqMap['article-beginner-guide'];
  };

  return (
    <>
      <ScrollToTop />
      <Helmet
        title="分类浏览 - 52662鼎盛平台 | 平台资讯、新手指南与活动专区"
        description="浏览52662鼎盛平台全部分类内容，包括平台资讯、新手指南、安全保障、会员权益与活动专区。了解鼎盛平台最新动态与使用攻略。"
        canonicalUrl="/category"
      />
      <CategorySchema />
      <Navbar />
      <main className="pt-16">
        <Breadcrumb />

        <section className="py-12 md:py-16 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-3">
                分类浏览
              </h1>
              <p className="text-foreground-600 text-sm max-w-xl mx-auto">
                选择分类查看相关内容，获取平台最新资讯与实用指南
              </p>
            </div>

            <div className="flex items-center justify-center gap-2 mb-12 flex-wrap">
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setActiveCategory(cat.id)}
                  className={`whitespace-nowrap px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer flex items-center gap-2 ${
                    activeCategory === cat.id
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200 hover:text-foreground-800'
                  }`}
                >
                  <i className={`${cat.icon} text-sm`} />
                  {cat.name}
                </button>
              ))}
            </div>

            {currentCategory && (
              <div className="text-center mb-10">
                <p className="text-foreground-500 text-sm">{currentCategory.description}</p>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredArticles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.id}`}
                  className="group bg-background-50 border border-background-200/50 rounded-lg overflow-hidden hover:border-primary-200/50 transition-all duration-300"
                >
                  <div className="relative w-full h-48 overflow-hidden">
                    <img
                      src={getArticleImage(article)}
                      alt={article.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-3 left-3">
                      <span className="px-2.5 py-1 rounded-full bg-background-950/60 text-background-50 text-xs font-medium">
                        {categories.find((c) => c.id === article.categoryId)?.name}
                      </span>
                    </div>
                  </div>
                  <div className="p-5">
                    <h3 className="font-semibold text-foreground-900 text-sm md:text-base mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors duration-200">
                      {article.title}
                    </h3>
                    <p className="text-foreground-500 text-xs leading-relaxed mb-4 line-clamp-2">
                      {article.summary}
                    </p>
                    <div className="flex items-center justify-between text-xs text-foreground-400">
                      <span>{article.date}</span>
                      <span className="flex items-center gap-1">
                        <i className="ri-eye-line text-xs" />
                        {article.readCount.toLocaleString()}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {filteredArticles.length === 0 && (
              <div className="text-center py-16">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
                  <i className="ri-inbox-line text-2xl text-foreground-400" />
                </div>
                <p className="text-foreground-500 text-sm">该分类暂无内容</p>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}